
import React, { useState } from 'react';
import { ViewState, Language } from '../types';
import { translations } from '../utils/translations';

interface HeroProps {
  setView: (view: ViewState) => void;
  language: Language;
  isReelOpen?: boolean;
  onReelClose?: () => void;
}

export const Hero: React.FC<HeroProps> = ({ setView, language, isReelOpen: isReelOpenProp, onReelClose }) => {
  const [isReelOpenInternal, setIsReelOpenInternal] = useState(false);
  
  // Use prop if provided, otherwise use internal state
  const isReelOpen = isReelOpenProp !== undefined ? isReelOpenProp : isReelOpenInternal;
  const setReelOpen = (open: boolean) => {
    if (open) {
      if (setView) setView(ViewState.DEMO_REEL);
      else setIsReelOpenInternal(true);
    } else {
      if (onReelClose) onReelClose();
      else if (setView) setView(ViewState.HOME);
      else setIsReelOpenInternal(false);
    }
  };

  const t = translations[language];
  // Accessing BASE_URL
  const baseUrl = (import.meta as any).env?.BASE_URL || '/';

  const handleCtaClick = () => {
    const element = document.getElementById('directors');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // URL del Demo Reel oficial de ¡RR3VERENT!A
  const reelYoutubeUrl = "https://www.youtube.com/embed/HDcY7GzxHjY?si=mb0jGxenRppRPWsZ&autoplay=1&rel=0";

  return (
    <div className="relative min-h-screen w-full bg-black flex flex-col pt-20 overflow-hidden scanlines">
      
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full z-0 overflow-hidden">
        <video 
          autoPlay 
          loop 
          muted 
          playsInline 
          className="w-full h-full object-cover opacity-100"
        >
          <source src="https://www.irreverentia.eu/hero-bg.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/30 bg-gradient-to-b from-black/60 via-transparent to-black/90"></div>
      </div>

      <div className="flex-grow flex flex-col justify-center px-6 lg:px-12 max-w-[1400px] mx-auto w-full z-10 relative">
        
        <div className="mb-6 md:mb-12 lg:mb-16 flex flex-col items-start">
          <div className="relative mt-2">
             <img 
               src="https://www.irreverentia.eu/logo-green2.png" 
               alt="IRREVERENTIA"
               className="w-full max-w-[85vw] md:max-w-[750px] h-auto object-contain drop-shadow-[0_0_35px_rgba(204,255,0,0.6)] relative z-10 logo-hero-effect"
               onError={(e) => {
                 e.currentTarget.style.display = 'none';
               }}
             />
          </div>
        </div>

        {/* Large Display Statement outside the frame - Styled with VHS VCR OSD Font */}
        <div className="max-w-5xl mb-8 md:mb-14 lg:mb-16">
          <h1 className="font-vcr text-[18px] sm:text-[24px] md:text-[32px] lg:text-[38px] uppercase tracking-wider leading-[1.25] drop-shadow-[0_4px_24px_rgba(0,0,0,0.8)]">
            <span className="text-white">{t.hero.headlineStart}</span>
            <span className="text-[#b026ff] animate-purple-pulse drop-shadow-[0_0_25px_rgba(176,38,255,0.75)]">
              {t.hero.headlineHighlight}
            </span>
          </h1>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 md:gap-10 mb-8 md:mb-14 lg:mb-20">
          {/* Tagline box - hidden on mobile screens */}
          <div className="hidden md:block border-l-[4px] border-[#ccff00] pl-6 max-w-2xl backdrop-blur-sm bg-black/50 p-6 shadow-2xl border border-zinc-800/50">
            <p className="font-mono text-zinc-200 text-sm sm:text-base md:text-lg leading-relaxed">
              {t.hero.tagline}
            </p>
          </div>

          <div className="flex flex-wrap gap-3 sm:gap-4 shrink-0">
            {/* CTA 1: Filmmakers (Sección) */}
            <button 
              onClick={handleCtaClick}
              className="btn-8bit group flex items-center gap-2 sm:gap-3 text-sm sm:text-base md:text-xl px-5 sm:px-8 md:px-12 py-2.5 sm:py-3.5 md:py-5"
            >
              {t.hero.cta}
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                fill="none" 
                viewBox="0 0 24 24" 
                strokeWidth={3} 
                stroke="currentColor" 
                className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
              >
                <path strokeLinecap="square" strokeLinejoin="miter" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
              </svg>
            </button>

            {/* CTA 2: Demo Reel (Modal) */}
            <button 
              onClick={() => setReelOpen(true)}
              className="btn-8bit !bg-black !text-acid border-2 border-acid group flex items-center gap-2 sm:gap-3 text-sm sm:text-base md:text-xl px-5 sm:px-8 md:px-12 py-2.5 sm:py-3.5 md:py-5 hover:!bg-acid hover:!text-black"
            >
              {t.hero.demoReel}
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                fill="currentColor" 
                viewBox="0 0 24 24" 
                className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 group-hover:scale-125 transition-transform"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
          </div>
        </div>

      </div>

      <div className="relative w-full bg-[#b026ff] py-4 overflow-hidden transform -rotate-1 origin-bottom-left scale-105 border-y-4 border-black z-20 shadow-2xl">
        <div className="marquee-content flex gap-8 whitespace-nowrap font-display font-black text-black text-2xl md:text-3xl uppercase tracking-tighter">
          {Array(8).fill(t.marquee).map((text, i) => (
             <span key={i} className="flex items-center">
               {text} <span className="mx-4 text-white">■</span>
             </span>
          ))}
        </div>
      </div>

      {/* REEL MODAL */}
      {isReelOpen && (
        <div 
          className="fixed inset-0 z-[1000] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-12 animate-glitch-shot"
          onClick={() => setReelOpen(false)}
        >
           {/* Close Button */}
           <button 
             onClick={() => setReelOpen(false)}
             className="absolute top-6 right-6 text-white hover:text-acid z-[1010] p-4 group"
           >
             <div className="relative w-10 h-10 flex items-center justify-center">
                <span className="absolute w-full h-1 bg-white rotate-45 group-hover:bg-acid group-hover:animate-pulse"></span>
                <span className="absolute w-full h-1 bg-white -rotate-45 group-hover:bg-acid group-hover:animate-pulse"></span>
             </div>
             <span className="block font-mono text-[10px] mt-2 tracking-widest uppercase">CLOSE_ESC</span>
           </button>

           <div 
             className="w-full h-full max-w-[1600px] flex flex-col justify-center"
             onClick={(e) => e.stopPropagation()}
           >
              <div className="relative aspect-video w-full bg-black border-4 border-zinc-800 shadow-[0_0_80px_rgba(204,255,0,0.2)]">
                {/* HUD Elements */}
                <div className="absolute -top-4 -left-4 bg-acid text-black font-mono text-[10px] px-3 py-1 font-bold z-10 border-2 border-black">
                   SIGNAL: LIVE_STREAM_v4.2
                </div>
                <div className="absolute -bottom-4 -right-4 bg-[#b026ff] text-white font-mono text-[10px] px-3 py-1 font-bold z-10 border-2 border-black">
                   RESOLUTION: NEURAL_UPSCALED
                </div>

                <iframe 
                  src={reelYoutubeUrl} 
                  className="w-full h-full" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                  allowFullScreen
                  title="IRREVERENTIA DEMO REEL"
                ></iframe>
              </div>
           </div>
        </div>
      )}
      
    </div>
  );
};
